import { hasUnpricedItem } from '@klear/payment/item'
import { Plus, X } from 'lucide-react'
import { Money } from '@klear/money'
import type { CurrencyCode } from '@klear/constants/currency'
import {
  PAYMENT_LINE_ITEM_TYPE_PURCHASE,
  PAYMENT_LINE_ITEM_TYPES,
  PAYMENT_LINE_ITEM_TYPE_LABELS
} from '@klear/payment/constants'
import type { PaymentItemWire } from '@klear/payment/item.wire'
import { Button } from '@klear/ui/components/button'
import { Input } from '@klear/ui/components/input'
import { MoneyInput } from '@klear/ui/components/money-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@klear/ui/components/select'

type PayableCostRowsProps = {
  items: PaymentItemWire[]
  currency: CurrencyCode
  readOnly?: boolean
  onChange: (items: PaymentItemWire[]) => void
}

export function PayableCostRows({ items, currency, readOnly = false, onChange }: PayableCostRowsProps) {
  const hasZeroAmount = hasUnpricedItem(items)

  const patch = (index: number, next: Partial<PaymentItemWire>) => {
    onChange(items.map((item, i) => (i === index ? { ...item, ...next } : item)))
  }

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2 rounded-md border border-dashed border-border p-2">
          <Select
            value={item.type}
            onValueChange={(value) => patch(index, { type: value as PaymentItemWire['type'] })}
            disabled={readOnly}
          >
            <SelectTrigger className="w-36 shrink-0" aria-label="Line item type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAYMENT_LINE_ITEM_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {PAYMENT_LINE_ITEM_TYPE_LABELS[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            value={item.note ?? ''}
            onChange={(e) => patch(index, { note: e.target.value.length > 0 ? e.target.value : undefined })}
            placeholder="What it's for (optional)"
            disabled={readOnly}
            className="min-w-0 flex-1"
            aria-label="Line item note"
          />

          <MoneyInput
            value={new Money(item.amount)}
            onChange={(money) => patch(index, { amount: money.toJSON() })}
            label=""
            lockCurrency
            allowNegative
            disabled={readOnly}
            className="w-32 shrink-0 space-y-0"
          />

          {readOnly ? null : (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => onChange(items.filter((_, i) => i !== index))}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Remove this line item</span>
            </Button>
          )}
        </div>
      ))}

      {readOnly ? null : (
        <Button
          variant="outline"
          size="sm"
          disabled={hasZeroAmount}
          onClick={() =>
            onChange([...items, { type: PAYMENT_LINE_ITEM_TYPE_PURCHASE, amount: Money.zero(currency).toJSON() }])
          }
        >
          <Plus className="h-4 w-4 mr-1" />
          Add a line item
        </Button>
      )}
    </div>
  )
}
